import React, { useMemo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';

import { useTheme } from '../../../../contexts/ThemeContext';
import { usePersonaChatUI } from '../../../../contexts/PersonaChatUIContext';
import type { SpineFanChipData } from '../types';

/**
 * SpineFanChipNode — progressive-disclosure chip for N>=3 sibling spine fans.
 *
 * Click flips ChatDagRuntimeContext.collapsedFanIds via the shared
 * toggleFanExpansion(parentFanKey) helper — same mechanism as the
 * lateral FanCollapsedChip (do NOT duplicate the toggle code).
 *
 * Deterministic parentFanKey ensures the chip's identity stays stable
 * across re-renders.
 */
const SpineFanChipNode: React.FC<NodeProps<SpineFanChipData>> = ({ data }) => {
  const { darkMode } = useTheme();
  const { toggleFanExpansion } = usePersonaChatUI();

  // SP3-MF-06 — flavor-driven label. Existing fans backfill to
  // 'proposals'; ChatDAGRAGDAG squad-context fans set 'squad'.
  const label = useMemo(() => {
    if (data.flavor === 'squad') {
      return `${data.childCount} squad members`;
    }
    if (data.flavor === 'proposals') {
      return `${data.childCount} proposals approved here`;
    }
    // Fall-through / 'generic' / undefined — neutral copy.
    return `${data.childCount} branches`;
  }, [data.flavor, data.childCount]);

  return (
    <div
      className={`nodrag relative cursor-pointer select-none rounded-md border px-2.5 py-2 text-[11px] hover:-translate-y-0.5 transition-transform shadow-sm ${
        darkMode
          ? 'bg-gray-800 border-gray-600/80 text-gray-200 hover:border-emerald-500/60'
          : 'bg-white border-gray-300 text-gray-800 hover:border-emerald-500/60'
      }`}
      style={{ width: 240 }}
      data-testid={`spine-fan-chip-${data.parentFanKey}`}
      onClick={() => toggleFanExpansion(data.parentFanKey)}
    >
      <Handle type="target" id="t" position={Position.Top} style={{ opacity: 0 }} />
      <div className="flex items-center gap-1.5">
        <span aria-hidden className="text-[14px]">▼</span>
        <span className="font-medium" style={{ flex: 1 }}>
          {label}
        </span>
      </div>
      <div className={`text-[9px] mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        click to expand
      </div>
      <Handle type="source" id="b" position={Position.Bottom} style={{ opacity: 0 }} />
    </div>
  );
};

export default SpineFanChipNode;
